import {
  OBRAS_TABLE_COLUMNS_KEY,
  OBRAS_TABLE_DENSITY_KEY,
  OBRAS_TABLE_SETTINGS_FORMAT_KEY,
} from "@/core/constants/keys";
import type { FilterValue } from "@/ds";
import type { ObrasFilters } from "@/modules/obras";
import {
  OBRAS_DEFAULT_OPTIONAL_COLUMNS,
  OBRAS_NO_VALUE,
  PERIOD_COLUMN,
} from "./obras-lista.constants";
import type { DateRangeValue } from "./types-obras-lista";

/** Conjunto completo de filtros no formato dos filtros salvos (`Record<string, string[]>`). */
export function toSavedValue(filters: ObrasFilters): FilterValue {
  return {
    statusObra: filters.statusObra,
    embarcacaoId: filters.embarcacaoId,
    tipoObra: filters.tipoObra,
    dataInicio: filters.dataInicio ? [filters.dataInicio] : [],
    dataFim: filters.dataFim ? [filters.dataFim] : [],
  };
}

/** Critérios no formato do painel de filtros: o período entra como `[início, fim]`. */
export function toPanelValue(filters: ObrasFilters): FilterValue {
  return {
    ...toSavedValue(filters),
    [PERIOD_COLUMN]:
      filters.dataInicio && filters.dataFim ? [filters.dataInicio, filters.dataFim] : [],
  };
}

export function fromSavedValue(value: FilterValue): ObrasFilters {
  return {
    statusObra: value.statusObra ?? [],
    embarcacaoId: value.embarcacaoId ?? [],
    tipoObra: value.tipoObra ?? [],
    dataInicio: value.dataInicio?.[0] ?? "",
    dataFim: value.dataFim?.[0] ?? "",
  };
}

export function rangeFromFilters(filters: ObrasFilters): DateRangeValue {
  const start = fromIsoDate(filters.dataInicio);
  const end = fromIsoDate(filters.dataFim);
  return start || end ? [start, end] : null;
}

/** `yyyy-MM-dd` → `dd/MM/aa` (protótipo). */
export function formatObraDate(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return OBRAS_NO_VALUE;
  return `${match[3]}/${match[2]}/${(match[1] ?? "").slice(2)}`;
}

/** `yyyy-MM-dd` → `dd/MM/aaaa` (resumo de filtros do arquivo exportado). */
export function formatObraDateLong(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : iso;
}

export function formatInteger(value: number | null): string {
  // Figma 5651:228915: inteiros sem separador de milhar ("1615").
  return value === null
    ? OBRAS_NO_VALUE
    : new Intl.NumberFormat("pt-BR", { useGrouping: false }).format(Math.trunc(value));
}

export function formatPercent(value: number | null): string {
  return value === null
    ? OBRAS_NO_VALUE
    : `${new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 1 }).format(value)}%`;
}

/** Realizado BRL M: já em milhões de reais (RN09). */
export function formatMillions(value: number | null): string {
  return value === null
    ? OBRAS_NO_VALUE
    : new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(
        value,
      );
}

export function formatDuration(days: number | null): string {
  return days === null ? OBRAS_NO_VALUE : String(days);
}

/** `yyyy-MM-dd` a partir de uma data local. */
export function toIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

export function fromIsoDate(iso: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
}

/** Resumo do período filtrado para o arquivo exportado; vazio sem período. */
export function formatObraPeriodo(filters: Pick<ObrasFilters, "dataInicio" | "dataFim">) {
  if (!filters.dataInicio && !filters.dataFim) return undefined;

  const start = filters.dataInicio ? formatObraDateLong(filters.dataInicio) : "…";
  const end = filters.dataFim ? formatObraDateLong(filters.dataFim) : "…";
  return `${start} a ${end}`;
}

/**
 * Marca que as preferências da tabela já estão no formato do `useTableSettings` do DS (densidade
 * `normal`/`small` em JSON; colunas OCULTAS). Sem a marca, o formato é o antigo (`confortavel`/`compacto`
 * crus; colunas opcionais VISÍVEIS) e o valor é convertido uma única vez.
 */
const SETTINGS_FORMAT_VERSION = "ds-v1";

const LEGACY_DENSITY: Record<string, "normal" | "small"> = {
  confortavel: "normal",
  compacto: "small",
};

/** Densidade antiga (crua ou entre aspas) para a do DS; qualquer outro valor fica como está. */
function migrateDensity(storage: Storage) {
  const raw = storage.getItem(OBRAS_TABLE_DENSITY_KEY);
  const migrated = raw === null ? undefined : LEGACY_DENSITY[raw.replace(/^"|"$/g, "")];
  if (migrated) storage.setItem(OBRAS_TABLE_DENSITY_KEY, JSON.stringify(migrated));
}

/** Colunas opcionais visíveis (formato antigo) para a lista de ocultas; valor inválido volta ao padrão. */
function migrateColumns(storage: Storage) {
  if (storage.getItem(OBRAS_TABLE_SETTINGS_FORMAT_KEY) === SETTINGS_FORMAT_VERSION) return;

  const raw = storage.getItem(OBRAS_TABLE_COLUMNS_KEY);
  if (raw !== null) {
    try {
      const visible: unknown = JSON.parse(raw);
      if (
        Array.isArray(visible) &&
        visible.every((key) => OBRAS_DEFAULT_OPTIONAL_COLUMNS.includes(key as string))
      ) {
        const hidden = OBRAS_DEFAULT_OPTIONAL_COLUMNS.filter((key) => !visible.includes(key));
        storage.setItem(OBRAS_TABLE_COLUMNS_KEY, JSON.stringify(hidden));
      } else {
        storage.removeItem(OBRAS_TABLE_COLUMNS_KEY);
      }
    } catch {
      storage.removeItem(OBRAS_TABLE_COLUMNS_KEY);
    }
  }
  storage.setItem(OBRAS_TABLE_SETTINGS_FORMAT_KEY, SETTINGS_FORMAT_VERSION);
}

/**
 * Converte, na leitura, as preferências salvas antes do `useTableSettings` do DS: `confortavel` vira
 * `normal`, `compacto` vira `small` (mesma chave) e as colunas visíveis viram a lista de ocultas, para
 * ninguém perder a configuração. Roda antes do hook; sem armazenamento disponível não faz nada.
 */
export function migrateObrasTableSettings(): void {
  try {
    migrateDensity(localStorage);
    migrateColumns(localStorage);
  } catch {
    // Sem armazenamento (modo privativo): o hook cai nos padrões.
  }
}
